import { Anchor, Box, Button, Card, Container, Group, Stack, Table, Text, ThemeIcon, Title } from '@mantine/core'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LuPackage, LuTriangleAlert } from 'react-icons/lu'
import { DataTable } from '@/frontend/components/marina/DataTable'
import { PillNav } from '@/frontend/components/marina/PillNav'
import { StatusBadge } from '@/frontend/components/marina/StatusBadge'
import { useCanWrite } from '@/frontend/hooks/useAuth'
import { useInventory } from '@/frontend/hooks/useInventory'

type StockFilter = 'all' | 'low' | 'out'

export const Route = createFileRoute('/dashboard/facility/inventory/')({
  component: InventoryList,
})

function InventoryList() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const canWrite = useCanWrite()
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [stockFilter, setStockFilter] = useState<StockFilter>('all')

  const { data, isLoading } = useInventory({ q: query })
  const allItems = data?.items ?? []

  const items = allItems.filter((it) => {
    if (stockFilter === 'out') return it.thresholdStatus === 'OUT'
    if (stockFilter === 'low') return it.thresholdStatus === 'LOW'
    return true
  })

  const outCount = allItems.filter((it) => it.thresholdStatus === 'OUT').length
  const lowCount = allItems.filter((it) => it.thresholdStatus === 'LOW').length

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Group gap="sm" align="center">
            <ThemeIcon variant="light" color="laut" size="lg" radius="md">
              <LuPackage size={18} />
            </ThemeIcon>
            <div>
              <Title order={2} ff="var(--font-display)" fw={600}>
                {t('page.facility.inventory.title')}
              </Title>
              <Text size="xs" c="dimmed">
                {t('page.facility.inventory.subtitle')}
              </Text>
            </div>
          </Group>
          {canWrite && (
            <Button
              color="karang"
              size="sm"
              onClick={() => navigate({ to: '/dashboard/facility/inventory/new' as any })}
            >
              {t('page.facility.inventory.btn_new')}
            </Button>
          )}
        </Group>

        {(outCount > 0 || lowCount > 0) && (
          <Box
            px="md"
            py="xs"
            style={{
              borderRadius: 10,
              background: 'light-dark(var(--mantine-color-orange-0), rgba(200,120,40,0.1))',
              cursor: 'pointer',
            }}
            onClick={() => setStockFilter(outCount > 0 ? 'out' : 'low')}
          >
            <Group gap="xs" justify="space-between">
              <Group gap="xs">
                <ThemeIcon variant="light" color="orange" size="sm" radius="xl">
                  <LuTriangleAlert size={13} />
                </ThemeIcon>
                <Text size="sm" fw={600} c="orange.7">
                  {outCount > 0 && t('page.facility.inventory.alert_out', { n: outCount })}
                  {outCount > 0 && lowCount > 0 && ' · '}
                  {lowCount > 0 && t('page.facility.inventory.alert_low', { n: lowCount })}
                </Text>
              </Group>
              <Text size="xs" c="orange.6" fw={500}>
                {t('common.see_more')}
              </Text>
            </Group>
          </Box>
        )}

        <Group justify="space-between" wrap="wrap" gap="sm">
          <PillNav
            value={stockFilter}
            onChange={setStockFilter}
            items={[
              {
                value: 'all',
                label: t('page.facility.inventory.tab_all'),
                count: allItems.length || undefined,
                countColor: 'gray',
              },
              { value: 'out', label: t('page.facility.inventory.tab_out'), count: outCount, countColor: 'red' },
              { value: 'low', label: t('page.facility.inventory.tab_low'), count: lowCount, countColor: 'orange' },
            ]}
          />
          <input
            type="search"
            value={search}
            placeholder={t('page.facility.inventory.placeholder')}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setQuery(search)
            }}
            style={{
              fontSize: '0.8rem',
              padding: '6px 12px',
              borderRadius: 8,
              border: '1px solid var(--mantine-color-default-border)',
              background: 'var(--app-card-bg)',
              color: 'inherit',
              width: 220,
              outline: 'none',
            }}
          />
        </Group>

        <Card radius="md" padding="lg">
          <DataTable
            isEmpty={!isLoading && items.length === 0}
            emptyLabel={t('page.facility.inventory.empty')}
            page={1}
            pageSize={items.length || 1}
            total={items.length}
            columns={
              <>
                <Table.Th>{t('page.facility.inventory.col_item')}</Table.Th>
                <Table.Th>{t('page.facility.inventory.col_category')}</Table.Th>
                <Table.Th ta="right">{t('page.facility.inventory.col_stock')}</Table.Th>
                <Table.Th>{t('page.facility.inventory.col_status')}</Table.Th>
                <Table.Th>{t('page.facility.inventory.col_updated')}</Table.Th>
                <Table.Th ta="right">{t('common.actions')}</Table.Th>
              </>
            }
          >
            {items.map((it) => (
              <Table.Tr
                key={it.id}
                style={{ cursor: 'pointer' }}
                onClick={() => navigate({ to: `/dashboard/facility/inventory/${it.id}` as any })}
              >
                <Table.Td>
                  <Text size="sm" fw={600}>
                    {it.name}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{it.category ?? '—'}</Text>
                </Table.Td>
                <Table.Td ta="right">
                  <Text
                    size="sm"
                    fw={600}
                    c={it.thresholdStatus === 'OUT' ? 'red.6' : it.thresholdStatus === 'LOW' ? 'orange.6' : undefined}
                  >
                    {Number(it.currentStock).toFixed(1)}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {it.unit}
                  </Text>
                </Table.Td>
                <Table.Td>
                  {it.thresholdStatus && <StatusBadge status={it.thresholdStatus} kind="inventoryThreshold" upper />}
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{dayjs(it.updatedAt).format('DD MMM YYYY')}</Text>
                </Table.Td>
                <Table.Td ta="right" onClick={(e) => e.stopPropagation()}>
                  <Anchor
                    component={Link}
                    to={`/dashboard/facility/inventory/${it.id}` as any}
                    c="laut"
                    size="sm"
                    fw={500}
                  >
                    {t('common.see_more')}
                  </Anchor>
                </Table.Td>
              </Table.Tr>
            ))}
          </DataTable>
        </Card>
      </Stack>
    </Container>
  )
}
